import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Day tabs for the inpatient stay sections.
 *
 * Stays of 7 days or fewer render one tab per day. Longer stays collapse each
 * full week — except the last stretch, which always shows individual day tabs —
 * into a "Week N" dropdown menu (e.g. 10 days: [Week 1 ▾] [Day 8] [Day 9] [Day 10]).
 * The menu portals to document.body because the tabs container's overflow
 * would otherwise clip it.
 */
const WeekDayTabs = ({ totalDays, activeDay, onSelect, getDayDate, isDayLocked }) => {
    const { t } = useTranslation();
    const [openWeek, setOpenWeek] = useState(null);
    const [menuPos, setMenuPos] = useState(null);
    const wrapRef = useRef(null);
    const menuRef = useRef(null);
    const weekBtnRefs = useRef({});

    const openMenu = (weekNo) => {
        if (openWeek === weekNo) {
            setOpenWeek(null);
            setMenuPos(null);
            return;
        }
        const btn = weekBtnRefs.current[weekNo];
        if (btn) {
            const rect = btn.getBoundingClientRect();
            setMenuPos({ top: rect.bottom + 4, left: rect.left });
        }
        setOpenWeek(weekNo);
    };

    // Close an open week menu when clicking elsewhere, scrolling, or resizing
    useEffect(() => {
        if (openWeek === null) return undefined;
        const close = (e) => {
            const inWrap = wrapRef.current && wrapRef.current.contains(e.target);
            const inMenu = menuRef.current && menuRef.current.contains(e.target);
            if (!inWrap && !inMenu) {
                setOpenWeek(null);
                setMenuPos(null);
            }
        };
        const closeAlways = () => {
            setOpenWeek(null);
            setMenuPos(null);
        };
        document.addEventListener('mousedown', close);
        window.addEventListener('scroll', closeAlways, true);
        window.addEventListener('resize', closeAlways);
        return () => {
            document.removeEventListener('mousedown', close);
            window.removeEventListener('scroll', closeAlways, true);
            window.removeEventListener('resize', closeAlways);
        };
    }, [openWeek]);

    const days = Math.max(1, totalDays);
    const WEEK = 7;
    const collapsedWeeks = days > WEEK ? Math.floor((days - 1) / WEEK) : 0;
    const individualStart = collapsedWeeks * WEEK + 1;

    const renderWeekMenu = (weekNo, start) => (
        createPortal(
            <div
                ref={menuRef}
                style={{
                position: 'fixed',
                top: menuPos?.top ?? 0,
                left: menuPos?.left ?? 0,
                zIndex: 100000,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.12)',
                minWidth: '160px',
                padding: '6px',
            }}>
                {Array.from({ length: WEEK }, (_, k) => start + k).map((i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={() => { onSelect(i); setOpenWeek(null); setMenuPos(null); }}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px',
                            width: '100%',
                            padding: '8px 10px',
                            border: 'none',
                            background: activeDay === i ? '#E6FAF6' : 'transparent',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            color: '#374151',
                        }}
                    >
                        <span>
                            {t('receptionInpatients.details.dailyCare.day', { count: i, defaultValue: `Day ${i}` })}
                        </span>
                        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>{getDayDate(i)}</span>
                    </button>
                ))}
            </div>,
            document.body
        )
    );

    return (
        <div ref={wrapRef} style={{ display: 'contents' }}>
            {Array.from({ length: collapsedWeeks }, (_, w) => {
                const weekNo = w + 1;
                const start = w * WEEK + 1;
                const end = start + WEEK - 1;
                const containsActive = activeDay >= start && activeDay <= end;
                return (
                    <div key={`week-${weekNo}`} style={{ position: 'relative' }}>
                        <button
                            type="button"
                            ref={(el) => { weekBtnRefs.current[weekNo] = el; }}
                            className={`day-tab-btn week-tab-btn ${containsActive ? 'active' : ''}`}
                            onClick={() => openMenu(weekNo)}
                        >
                            <span className="tab-label">
                                {t('receptionInpatients.details.dailyCare.week', { count: weekNo, defaultValue: `Week ${weekNo}` })}
                                <i className={`fas fa-chevron-${openWeek === weekNo ? 'up' : 'down'}`} style={{ marginLeft: '6px', fontSize: '10px' }}></i>
                            </span>
                            <span className="tab-date">{getDayDate(start)} – {getDayDate(end)}</span>
                        </button>
                        {openWeek === weekNo && menuPos ? renderWeekMenu(weekNo, start) : null}
                    </div>
                );
            })}
            {Array.from({ length: days - individualStart + 1 }, (_, k) => individualStart + k).map((i) => {
                const locked = isDayLocked ? isDayLocked(i) : false;
                return (
                    <button
                        key={i}
                        type="button"
                        onClick={() => onSelect(i)}
                        className={`day-tab-btn ${activeDay === i ? 'active' : ''} ${locked ? 'locked' : ''}`}
                    >
                        <span className="tab-label">
                            {t('receptionInpatients.details.dailyCare.day', { count: i, defaultValue: `Day ${i}` })}
                            {locked && <i className="fas fa-lock tab-lock" aria-hidden="true"></i>}
                        </span>
                        <span className="tab-date">{getDayDate(i)}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default WeekDayTabs;
